package dev.lingvo.shared

import kotlinx.serialization.Serializable

/**
 * What a workspace is on.
 *
 * The allowances here are the single source of truth — the server enforces them, the CLI and the web
 * app display them, and `docs/PRICING.md` describes them. If those ever disagree, this file is
 * right and the document is stale.
 */
@Serializable
enum class Plan(
    val displayName: String,
    /** Monthly price per billable seat, in whole dollars. */
    val pricePerSeat: Int,
    /** Credits granted per billable seat each month. */
    val creditsPerSeat: Int,
    /** Seats that may hold OWNER or ADMIN. Null means no limit. */
    val maxBillableSeats: Int?,
    /** Projects allowed. Null means no limit. */
    val maxProjects: Int?,
) {
    FREE("Free", pricePerSeat = 0, creditsPerSeat = 250, maxBillableSeats = 1, maxProjects = 1),
    STUDIO("Studio", pricePerSeat = 19, creditsPerSeat = 5_000, maxBillableSeats = null, maxProjects = null),
    TEAM("Team", pricePerSeat = 49, creditsPerSeat = 20_000, maxBillableSeats = null, maxProjects = null),
    ;

    /**
     * Credits included for a workspace holding [billableSeats] owner and admin seats.
     *
     * **Capped at [maxBillableSeats], and that cap is the whole point.** Nothing removes seats when a
     * workspace moves to a smaller plan, and [dev.lingvo.server.Routes.requireSeatAllowance] only
     * refuses *new* seats — so without this, a Studio workspace with six admins that drops to Free
     * would keep drawing `250 × 6 = 1,500` credits every month, for ever, against a plan that
     * includes 250. Uncapped, cancelling was the cheapest way to keep six seats' worth of machine
     * translation.
     */
    fun creditsFor(billableSeats: Int): Int {
        val cap = maxBillableSeats ?: Int.MAX_VALUE
        return creditsPerSeat * billableSeats.coerceIn(1, cap)
    }
}

/**
 * The longest source string that may be machine-translated in one go, in characters.
 *
 * A ceiling rather than a price, because past a certain length the problem is not cost but quality:
 * a 5,000-character blob is a document, the model will drift or truncate, and nobody reviewing the
 * result can tell which part changed. Localization strings are UI text.
 *
 * It is also the thing that makes [creditsForStrings] bounded. Without a cap, "per 1,000 characters"
 * still has no upper limit per request, so a single call could cost an unbounded amount.
 */
const val MAX_MT_SOURCE_CHARS = 2_000

/** Characters of source per credit, above the first one. */
const val CHARS_PER_CREDIT = 1_000

/**
 * The most keys one queued pre-translate may name explicitly.
 *
 * A different limit from the synchronous one, and larger, because the two fail differently. A
 * synchronous request holds an HTTP connection open while the provider works, so its cap is about
 * latency; a queued run hands back a job id immediately and its cap is about how much a single
 * request may commit the workspace to spending before anybody sees a number.
 *
 * In `:shared` so the browser can refuse a 1,001-key selection with the same number the server will,
 * rather than letting somebody build a selection the server then rejects wholesale.
 */
const val MAX_MT_PRETRANSLATE_KEYS = 1_000

/**
 * The largest single credit grant.
 *
 * In `:shared` so every surface renders the number it is judged by rather than restating it. 100,000
 * is five Team seats for a month; more than that is a plan change, not a correction — and a grant to
 * a workspace on the platform key spends Lingvo's money at the provider, so the bound is an operating
 * cost, not a formality.
 */
const val MAX_OPERATOR_GRANT = 100_000

/**
 * What one machine translation costs, before size.
 *
 * Weighted by engine because the engines differ by an order of magnitude in what they cost to run.
 * A flat credit would mean either overcharging for DeepL or losing money on Claude, and the second
 * is the one that ends badly.
 *
 * **Model tier is deliberately not in this price, and that is only safe because of a rule enforced
 * somewhere else.** Claude Opus and Claude Haiku cost the same three credits here. What makes that
 * survivable is that a customer cannot choose which one Lingvo's own key runs: `effectiveSettings`
 * strips a project's pinned model when it substitutes the platform key, so a call on our money always
 * runs the provider default. Without that, a project could pin the most expensive model on the
 * account and spend Lingvo's money at that model's rate — a customer choice on a bill they do not pay.
 *
 * The pairing is load-bearing: **if model pinning on the platform key is ever allowed, this function
 * must gain a tier multiplier in the same commit.** `PlatformKeyTest` fails if a pinned model — or a
 * project-nominated base URL, which is the same hole with a credential at the end of it — survives
 * platform substitution, so that decision cannot be made quietly.
 */
fun creditCost(provider: MtProvider): Int = when (provider) {
    MtProvider.CLAUDE, MtProvider.OPENAI -> 3
    MtProvider.DEEPL, MtProvider.GOOGLE, MtProvider.LIBRETRANSLATE, MtProvider.MYMEMORY -> 1
}

/**
 * What translating one string of [sourceChars] characters costs.
 *
 * **Size is in here because a flat per-string credit is unbounded spend.** Every provider bills us
 * by token or by character, so a workspace translating 2,000-character paragraphs cost us up to
 * twenty times what one translating button labels did, for the same one credit. That is not a
 * rounding error — it is the difference between a plan that makes money and one that loses it on
 * exactly the customers who use it most.
 *
 * The shape is deliberately generous at the short end, which is where nearly all UI text lives:
 * anything up to [CHARS_PER_CREDIT] characters costs the base rate, so "Save", "Payment complete"
 * and a full sentence are all one credit on DeepL. Only genuinely long strings step up, and
 * [MAX_MT_SOURCE_CHARS] caps how far that can go. A user who never writes a paragraph never notices
 * this exists, and that is the intent — the meter should be invisible to ordinary use and only bite
 * where the cost is real.
 *
 * Plurals charge for the longest form rather than the sum of all of them. Russian's four forms are
 * one translation request with one shared context, not four independent ones, and billing them as
 * four would penalise exactly the languages this product exists to get right.
 */
fun creditsForString(provider: MtProvider, sourceChars: Int): Int {
    val chargeable = sourceChars.coerceIn(0, MAX_MT_SOURCE_CHARS)
    // Ceiling division, floored at one: an empty or one-character string is still a request.
    val units = ((chargeable + CHARS_PER_CREDIT - 1) / CHARS_PER_CREDIT).coerceAtLeast(1)
    return creditCost(provider) * units
}

/** What a batch costs. [sourceLengths] is one entry per string, in characters. */
fun creditsForStrings(provider: MtProvider, sourceLengths: List<Int>): Int =
    sourceLengths.sumOf { length -> creditsForString(provider, length) }

/**
 * Where a paid plan stands with the payment processor.
 *
 * Kept separate from [Plan] because they answer different questions: the plan is what the workspace
 * is entitled to, the status is whether the money behind it is current. Collapsing them into a
 * single `PAST_DUE` plan would mean a failed card silently rewrote what the customer had bought.
 */
@Serializable
enum class PlanStatus {
    /** Free, or paid and paying. Full entitlement. */
    ACTIVE,

    /** In a free trial. Full entitlement; no money has moved yet. */
    TRIALING,

    /**
     * A payment failed and the processor is retrying.
     *
     * **Entitlement is unchanged.** Paddle retries a failed card for roughly three weeks; taking
     * features away on day one would punish an expired card, which is the single most common and
     * least deliberate reason to land here. If the retries all fail Paddle cancels the subscription
     * and [CANCELED] applies, which is when entitlement actually drops.
     */
    PAST_DUE,

    /** Scheduled to end at [UsageDto.renewsAt]; still paid until then. */
    CANCELING,

    /** Over. The workspace is back on [Plan.FREE] and its allowance with it. */
    CANCELED,

    /** Paused by the customer. Entitlement follows the plan the workspace was dropped to. */
    PAUSED,
    ;

    /**
     * Whether the processor can still take money for this subscription.
     *
     * The only question worth asking before destroying a workspace, and one the status alone was
     * being used to answer wrongly: PAUSED and CANCELING both stop future charges, so refusing to
     * delete with "deleting a workspace does not stop a payment" was false in both — and, because a
     * paused subscription keeps its id, permanent.
     *
     * PAST_DUE is included deliberately. Paddle retries a failed card for about three weeks, so a
     * charge may still land; entitlement survives it for exactly the same reason.
     */
    val stillBills: Boolean
        get() = this == ACTIVE || this == TRIALING || this == PAST_DUE
    ;

    /** Whether this status should nudge the customer to fix their payment method. */
    val needsAttention: Boolean get() = this == PAST_DUE
}

/** A workspace's plan, seats and credit position — everything the billing surfaces need. */
@Serializable
data class UsageDto(
    val plan: Plan,
    /** Owners and admins. Translators and viewers are never billed. */
    val billableSeats: Int,
    val totalMembers: Int,
    val projects: Int,
    val creditsUsed: Int,
    val creditsIncluded: Int,
    /** Granted on top of the plan's allowance, for this month only. Same reset, different origin. */
    val creditsGranted: Int = 0,
    /** First day of the current UTC month, ISO-8601. */
    val periodStart: String,
    /** When the counter resets, ISO-8601. */
    val periodEnd: String,
    val status: PlanStatus = PlanStatus.ACTIVE,
    /**
     * Whether this workspace has a subscription at the processor.
     *
     * Drives which control the billing screen offers — "Upgrade" opens a checkout, "Manage billing"
     * opens the processor's portal. The subscription id itself is never sent to the browser.
     */
    val hasSubscription: Boolean = false,
    /** When the subscription next bills, or when a cancelling one ends. ISO-8601, or null. */
    val renewsAt: String? = null,
    /**
     * Whether a checkout can be started at all.
     *
     * False when the deployment has no payment processor configured, so the web app can explain
     * that rather than opening a checkout that cannot complete.
     */
    val checkoutAvailable: Boolean = false,
) {
    val creditsRemaining: Int get() = (creditsIncluded + creditsGranted - creditsUsed).coerceAtLeast(0)
    val monthlyCost: Int get() = plan.pricePerSeat * billableSeats
}

/** Asks for a checkout link for [plan]. The seat count is taken from the workspace, never the client. */
@Serializable
data class StartCheckoutRequest(val plan: Plan)

/** Where to send the browser to complete payment. */
@Serializable
data class CheckoutDto(val url: String)
